/**
 * Serialize + sanitize the rich-text composer's `contentEditable` output down
 * to a small, safe HTML subset before it's sent.
 *
 * The composer is a plain `contentEditable` driven by `document.execCommand`
 * (deliberately no editor library — the widget is a size-sensitive embeddable
 * bundle). `execCommand` emits inconsistent, browser-specific markup
 * (`<b>`/`<strong>`, `<div>` line wrappers, styled `<span>`s), so we normalise
 * it to ONE canonical tag set and drop everything else:
 *
 *   inline:  strong · em · u · s · code · a[href]
 *   block:   p · ul · ol · li · br
 *
 * This is defence-in-depth, not the only line of defence — the send content is
 * also sanitised on the backend and rendered through the safe
 * `html-react-parser` pipeline (never `dangerouslySetInnerHTML`). Its job here
 * is to keep what we send clean and predictable.
 */

/** Canonical tag a raw element maps to, or `null` to unwrap (keep children,
 *  drop the wrapper). `br` is handled as a void element separately. */
const TAG_MAP: Record<string, string | null> = {
  STRONG: "strong",
  B: "strong",
  EM: "em",
  I: "em",
  U: "u",
  S: "s",
  STRIKE: "s",
  DEL: "s",
  CODE: "code",
  A: "a",
  UL: "ul",
  OL: "ol",
  LI: "li",
  P: "p",
  DIV: "p",
  BR: "br",
};

/** Tags that mark the content as genuinely "rich" (vs. plain typed text). */
const RICH_TAGS = new Set(["strong", "em", "u", "s", "code", "a", "ul", "ol"]);

/** Elements dropped ENTIRELY — tag AND text content — rather than unwrapped.
 *  An unknown wrapper (span, font…) is unwrapped so its visible text survives,
 *  but a `<script>` / `<style>` block's "children" are code, not prose: keeping
 *  them would dump raw JS/CSS into the message (harmless — never executed — but
 *  garbage). Pasting a whole page also drags in `<head>`/`<title>`; drop those
 *  too. (Not a security boundary — the allowlist already refuses to emit any of
 *  these tags — just a cleaner paste.) */
const DROP_CONTENT_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  "NOSCRIPT",
  "TEMPLATE",
  "IFRAME",
  "OBJECT",
  "EMBED",
  "HEAD",
  "TITLE",
]);

/** Accept only safe link schemes; everything else (notably `javascript:`) is
 *  dropped so the anchor degrades to unlinked text. Relative/anchor/protocol-
 *  relative links are allowed. */
function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (!href) return null;
  // Explicit safe schemes.
  if (/^(https?:|mailto:|tel:)/i.test(href)) return href;
  // Any OTHER explicit scheme (javascript:, data:, vbscript:, …) → reject.
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return null;
  // No scheme → relative / root-relative / protocol-relative / fragment → safe.
  return href;
}

/** Rebuild `source`'s children into `target`, applying the allowlist. */
function rebuild(source: Node, target: Node, doc: Document): void {
  source.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      target.appendChild(doc.createTextNode(node.textContent ?? ""));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as Element;

    // Drop script/style/etc. wholesale — tag AND content (see DROP_CONTENT_TAGS).
    if (DROP_CONTENT_TAGS.has(el.tagName)) return;

    const mapped = TAG_MAP[el.tagName];

    if (mapped === undefined || mapped === null) {
      // Unknown wrapper (span, font, …) → unwrap: keep its children inline.
      rebuild(el, target, doc);
      return;
    }
    if (mapped === "br") {
      target.appendChild(doc.createElement("br"));
      return;
    }

    const out = doc.createElement(mapped);
    if (mapped === "a") {
      const href = safeHref(el.getAttribute("href") ?? "");
      if (!href) {
        // Bad/blank href → unwrap to text rather than emit a dead anchor.
        rebuild(el, target, doc);
        return;
      }
      out.setAttribute("href", href);
    }
    rebuild(el, out, doc);

    // Drop empty inline wrappers execCommand loves to leave behind
    // (`<strong></strong>`), but keep structural/void ones (li, br).
    const structural = mapped === "li" || mapped === "ul" || mapped === "ol" || mapped === "p";
    if (!structural && !out.textContent?.trim() && !out.querySelector("br")) return;
    target.appendChild(out);
  });
}

/**
 * Sanitize composer HTML to the canonical subset. Returns cleaned HTML, or ""
 * when there's no content. Never throws — a parse failure yields "".
 */
export function sanitizeComposerHtml(rawHtml: string): string {
  if (!rawHtml) return "";
  try {
    const doc = new DOMParser().parseFromString(`<body>${rawHtml}</body>`, "text/html");
    const container = doc.createElement("div");
    rebuild(doc.body, container, doc);
    return container.innerHTML.trim();
  } catch {
    return "";
  }
}

/**
 * Convert ARBITRARY pasted HTML (from another site / editor) into our canonical
 * composer subset, so a paste lands in our styles instead of foreign markup.
 * Beyond `sanitizeComposerHtml`'s allowlist it pre-transforms what we don't
 * support into what we do:
 *   - `h1`…`h6` → a bold paragraph (headings unsupported → keep the emphasis)
 *   - `pre`     → inline `<code>` (no code blocks)
 * Then the standard rebuild keeps bold/italic/underline/strike/code/links/lists
 * and unwraps everything else. Returns "" on failure.
 */
export function htmlToComposerHtml(rawHtml: string): string {
  if (!rawHtml) return "";
  try {
    const doc = new DOMParser().parseFromString(`<body>${rawHtml}</body>`, "text/html");
    for (const h of doc.body.querySelectorAll("h1,h2,h3,h4,h5,h6")) {
      const p = doc.createElement("p");
      const strong = doc.createElement("strong");
      while (h.firstChild) strong.appendChild(h.firstChild);
      p.appendChild(strong);
      h.replaceWith(p);
    }
    for (const pre of doc.body.querySelectorAll("pre")) {
      const code = doc.createElement("code");
      code.textContent = pre.textContent ?? "";
      pre.replaceWith(code);
    }
    const container = doc.createElement("div");
    rebuild(doc.body, container, doc);
    return container.innerHTML.trim();
  } catch {
    return "";
  }
}

/** Whether sanitized composer HTML carries real formatting (bold/italic/
 *  underline/strike/link/list) rather than just plain paragraphs of text. When
 *  false, the caller sends plain text so an unformatted message stays plain. */
export function hasRichFormatting(sanitizedHtml: string): boolean {
  if (!sanitizedHtml) return false;
  return new RegExp(`<(${[...RICH_TAGS].join("|")})[\\s>]`, "i").test(sanitizedHtml);
}
